import {
  AppConfigurationClient,
  featureFlagPrefix,
  parseFeatureFlag,
} from "@azure/app-configuration";
import rg4js from "raygun4js";
import { useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import type { ExternalPartners } from "@/briefpoint-client";
import { type Dictionary, useConfigContext } from "@/contexts/ConfigContext";
import type { FirmResponse, UserResponse } from "@/lib/types";

const ENVIRONMENT: string = import.meta.env.VITE_ENVIRONMENT;

//TODO: Probably a better way to enumerate these
export const BRIDGE_FF = "Bridge";
export const AUTOOBJECT_FF = "AutoObject";
export const RICHTEXT_FF = "RichText";
export const RIBBON_FF = "Ribbon";
export const COMPLAINTS = "Complaints";
export const DOC_HANDLING = "DocHandling";
export const MULTI_CLIENT_FF = "MultiClientSupport";
export const PROPOUND_DEFINITION_FF = "PropoundDefinitions";
export const SIMILAR_REQUESTS_FF = "SimilarRequests";
export const SPANISH_FF = "SpanishTranslation";
export const SUPPLEMENTAL_FEATURES_FF = "SupplementalFeatures";
export const TEMPLATES_PROPOUNDING_FF = "TemplatesOnlyPropounding";
export const CLIENT_TASKS_FF = "ClientTasks";
export const HIGHLIGHT_FF = "Highlight";
export const USER_NOTIFICATION = "UserNotification";

type UseConfigType = [
  (partner: ExternalPartners) => ExternalPartnerConfig,
  () => Dictionary<(user?: UserResponse, firm?: FirmResponse) => boolean>,
];

export default function useConfig(): UseConfigType {
  const history = useHistory();
  const configCtx = useConfigContext();
  const { config, setConfig, featureFlags, setFeatureFlags } = configCtx;

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch featureFlags
  useEffect(() => {
    const loadConfig = async () => {
      try {
        // READONLY CONNECTIONSTRING ONLY!
        const client = new AppConfigurationClient(
          "Endpoint=https://bp-public-config.azconfig.io;Id=chcS;Secret=0VKVcLM9i3ehgwl4o9KEMbcOkDGJPKgTSNkBecO/rUg=",
        );

        const configs = client.listConfigurationSettings({
          labelFilter: ENVIRONMENT,
        });
        const configSettings: Dictionary<string> = {};
        const featureFlagSettings: Dictionary<
          (user?: UserResponse, firm?: FirmResponse) => boolean
        > = {};

        for await (const setting of configs) {
          const val =
            setting.value !== undefined
              ? setting
              : await client.getConfigurationSetting({
                  key: setting.key,
                  label: ENVIRONMENT,
                });

          if (val.key.startsWith(featureFlagPrefix) && val.value) {
            const flag = parseFeatureFlag(val);
            if (!flag.value.id) {
              continue;
            }
            if (flag.value.conditions?.clientFilters?.length) {
              const firmFilter = flag.value.conditions.clientFilters.find(
                (x) => x.name === "Firm",
              )?.parameters?.firmId;
              const userFilter = flag.value.conditions.clientFilters.find(
                (x) => x.name === "User",
              )?.parameters?.userId;

              if (firmFilter) {
                featureFlagSettings[flag.value.id] = (
                  _user?: UserResponse,
                  firm?: FirmResponse,
                ) => {
                  if (firm && (firmFilter as string).includes(firm.id)) {
                    return true;
                  }
                  return false;
                };
                continue;
              }

              if (userFilter) {
                featureFlagSettings[flag.value.id] = (
                  user?: UserResponse,
                  _firm?: FirmResponse,
                ) => {
                  if (user?.id && (userFilter as string).includes(user.id)) {
                    return true;
                  }
                  return false;
                };
                continue;
              }

              throw new Error(
                "I only know how to filter on Users (with 1 param 'userId' containing a comma separated list of enabled User IDs) or Firms (with 1 param 'firmId' containing a comma separated list of enabled Firm IDs) at the moment.",
              );
            }
            featureFlagSettings[flag.value.id] = (
              _user?: UserResponse,
              _firm?: FirmResponse,
            ) => flag.value.enabled;
          } else if (val.value) {
            configSettings[setting.key] = val.value;
          }
        }
        setConfig(configSettings);
        setFeatureFlags(featureFlagSettings);
      } catch (error) {
        history.push("/configuration-error");
        rg4js("send", {
          error: error,
        });
      }
    };

    if (!config) {
      loadConfig();
    }
  }, [config, history, setConfig, featureFlags, setFeatureFlags]);

  const getPartnerConfig = useCallback(
    (partner: ExternalPartners) => {
      const authBase = config?.[`ExternalPartnerConfig:${partner}:AuthBase`];
      const clientId = config?.[`ExternalPartnerConfig:${partner}:ClientId`];

      if (!authBase) {
        throw new Error(`No AuthBase config value found for ${partner}.`);
      }

      if (!clientId) {
        throw new Error(`No ClientId config value found for ${partner}.`);
      }

      return { partner, authBase, clientId };
    },
    [config],
  );

  const getFeatureFlags = useCallback(() => {
    return (
      featureFlags ??
      ({} as Dictionary<(user?: UserResponse, firm?: FirmResponse) => boolean>)
    );
  }, [featureFlags]);

  return [getPartnerConfig, getFeatureFlags];
}

export interface ExternalPartnerConfig {
  partner: ExternalPartners;
  authBase: string;
  clientId: string;
}
